import type { InjectionKey, Ref } from 'vue';

export type SheetNavDirection = 'forward' | 'back';

/**
 * Duration of a panel push/pop, in ms. Must stay in sync with the
 * `sheet-panel-*` animations in `tailwind.config.js`: the CSS keyframes drive
 * the horizontal slide, this value drives the height animation and the moment
 * the outgoing panel leaves the DOM.
 */
export const SHEET_PANEL_DURATION_MS = 300;

/** Same easing as the sheet's own enter/exit, so a push feels like the same object. */
export const SHEET_PANEL_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Duration of the sheet's own close animation (`sheet-slide-out`). The stack is
 * reset only once the sheet is fully off-screen, otherwise the content would
 * visibly snap back to the root panel while the sheet is still sliding down.
 */
export const SHEET_CLOSE_DURATION_MS = 500;

export interface SheetNavigationContext {
    /** Panel on top of the stack: the one the user is looking at. */
    current: Ref<string | null>;
    /** Panel playing its leave animation. Still mounted, positioned absolutely. */
    leaving: Ref<string | null>;
    /** Direction of the last navigation. Picks which pair of keyframes plays. */
    direction: Ref<SheetNavDirection>;
    /**
     * False until the first navigation. The very first panel must appear without
     * sliding: the sheet itself is already animating in underneath it.
     */
    animated: Ref<boolean>;
    canGoBack: Ref<boolean>;
    /** Depth of the stack, root included. */
    depth: Ref<number>;
    push: (name: string) => void;
    pop: () => void;
    /** Straight back to the root panel, no animation. */
    reset: () => void;
}

export const SHEET_NAVIGATION_KEY: InjectionKey<SheetNavigationContext> = Symbol('sheet-navigation');

/**
 * Push/pop navigation inside a single sheet. The sheet stays put; only its
 * content slides, so a nested drawer reads as going one level deeper rather
 * than as a second sheet stacked on the first.
 *
 * Modelled on the design's own structure: sixteen flat sibling sheets, three of
 * which carry a back chevron. There is no drawer-on-drawer anywhere, so a plain
 * array of panel names is enough.
 */
export default function (rootPanel: Ref<string | null | undefined>): SheetNavigationContext {
    const stack = ref<string[]>([]);
    const leaving = ref<string | null>(null);
    const direction = ref<SheetNavDirection>('forward');
    const animated = ref(false);

    let leavingTimer: ReturnType<typeof setTimeout> | null = null;

    const current = computed(() => stack.value[stack.value.length - 1] ?? rootPanel.value ?? null);
    const canGoBack = computed(() => stack.value.length > 1);
    const depth = computed(() => Math.max(stack.value.length, 1));

    // The outgoing panel has to outlive the navigation so its keyframes can play.
    // A timer rather than `animationend`: under `prefers-reduced-motion` the
    // animation is suppressed and that event would never fire.
    const scheduleLeavingCleanup = () => {
        if (leavingTimer) clearTimeout(leavingTimer);
        leavingTimer = setTimeout(() => {
            leaving.value = null;
            leavingTimer = null;
        }, SHEET_PANEL_DURATION_MS);
    };

    const navigate = (to: string[], way: SheetNavDirection) => {
        const from = current.value;
        stack.value = to;
        if (from && from !== current.value) leaving.value = from;
        direction.value = way;
        animated.value = true;
        scheduleLeavingCleanup();
    };

    const push = (name: string) => {
        if (name === current.value) return;
        const base = stack.value.length ? stack.value : rootPanel.value ? [rootPanel.value] : [];
        navigate([...base, name], 'forward');
    };

    const pop = () => {
        if (!canGoBack.value) return;
        navigate(stack.value.slice(0, -1), 'back');
    };

    const reset = () => {
        if (leavingTimer) clearTimeout(leavingTimer);
        leavingTimer = null;
        stack.value = [];
        leaving.value = null;
        direction.value = 'forward';
        animated.value = false;
    };

    onUnmounted(() => {
        if (leavingTimer) clearTimeout(leavingTimer);
    });

    return { current, leaving, direction, animated, canGoBack, depth, push, pop, reset };
}
